import * as THREE from 'three'
import { collectAssemblyNodeIdentityLookupIds } from '@puppycad/cad-core'
import type {
  AssemblyComponentHistorySnapshot,
  GlbHierarchyNode,
  ImportedModelResult,
} from '@puppycad/cad-core'

/** 导入装配结果类型。 */
type ComposedAssemblyImportedModelResult = Extract<ImportedModelResult, { kind: 'composed-assembly' }>

/**
 * 收集装配历史身份 lookup ID。
 *
 * @param values 待归一化的身份值。
 * @returns 可用于历史投影匹配的去重 lookup ID。
 */
function collectAssemblyHistoryIdentityLookupIds(values: readonly unknown[]): string[] {
  return values
    .flatMap((value) => collectAssemblyNodeIdentityLookupIds(value))
    .filter((value, index, array) => array.indexOf(value) === index)
}

/**
 * 规范化历史投影使用的装配节点 ID，但保留实例编号。
 *
 * @param value 待规范化的未知值。
 * @returns 可精确比较的装配节点 ID。
 */
function normalizeAssemblyHistoryExactNodeId(value: unknown): string {
  return typeof value === 'string'
    ? value.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').trim()
    : ''
}

/**
 * 安全解码历史身份 ID。
 *
 * @param value 原始历史身份 ID。
 * @returns 解码并规范化后的 ID；无法解码时返回规范化原值。
 */
function decodeAssemblyHistoryIdentitySafely(value: string): string {
  try {
    return normalizeAssemblyHistoryExactNodeId(decodeURIComponent(value))
  } catch {
    return normalizeAssemblyHistoryExactNodeId(value)
  }
}

/**
 * 收集不去实例编号的精确历史身份 ID。
 *
 * 设计说明：同源装配体多实例只能用完整实例路径区分，不能在第一轮匹配时消费去实例编号后的 lookup，
 * 否则 `A[0]/Part[0]` 与 `A[1]/Part[0]` 会塌缩成同一棵组件树。
 *
 * @param values 待归一化的身份值。
 * @returns 保留实例编号的精确 ID 集合。
 */
function collectAssemblyHistoryExactIdentityIds(values: readonly unknown[]): string[] {
  return values
    .flatMap((value) => {
      const normalized = normalizeAssemblyHistoryExactNodeId(value)
      if (!normalized) {
        return []
      }
      return [normalized, decodeAssemblyHistoryIdentitySafely(normalized)]
    })
    .filter((value, index, array) => value.length > 0 && array.indexOf(value) === index)
}

/**
 * 判断候选身份是否精确命中历史身份集合。
 *
 * @param exactIds 保留实例编号的历史身份集合。
 * @param value 待匹配的候选身份。
 * @returns 精确或解码后精确命中时返回 true。
 */
function matchesAssemblyHistoryExactIdentity(
  exactIds: ReadonlySet<string>,
  value: unknown,
): boolean {
  return collectAssemblyHistoryExactIdentityIds([value]).some((candidateId) => exactIds.has(candidateId))
}

/**
 * 判断候选身份值是否命中历史 lookup 集合。
 *
 * @param lookupIds 已归一化的历史 lookup 集合。
 * @param value 待匹配的候选身份值。
 * @returns 候选值任一归一化形式命中时返回 true。
 */
function matchesAssemblyHistoryIdentityLookup(
  lookupIds: ReadonlySet<string>,
  value: unknown,
): boolean {
  return collectAssemblyNodeIdentityLookupIds(value).some((candidateId) => lookupIds.has(candidateId))
}

/**
 * 收集装配历史快照中可用于定位组件的稳定 ID。
 *
 * 设计说明：
 * 1. 视口拖拽路径可能拿不到真实装配 tabId，此时 assemblyId 会退化为 default-assembly；
 * 2. componentInstanceId / sourceNodeId 仍然能表达被移动组件自身身份；
 * 3. 这里统一去重，避免后续 hierarchy / manifest 兜底匹配重复判断。
 *
 * @param snapshot 装配组件历史快照。
 * @returns 可用于组件身份匹配的 ID 列表。
 */
function collectSnapshotComponentIdentityIds(
  snapshot: AssemblyComponentHistorySnapshot,
): string[] {
  return collectAssemblyHistoryIdentityLookupIds([
    snapshot.componentInstanceId,
    snapshot.sourceNodeId,
  ])
}

/**
 * 判断导入层级树中是否包含指定组件 ID。
 *
 * @param node 当前层级节点。
 * @param candidateIds 组件候选 lookup ID。
 * @returns 命中任一层级节点时返回 true。
 */
function hasHierarchyNodeIdentity(
  node: GlbHierarchyNode,
  candidateIds: ReadonlySet<string>,
): boolean {
  const nodeIds = collectAssemblyHistoryIdentityLookupIds([
    node.objectId,
    node.path,
    node.meta?.selectionObjectId,
    node.meta?.assemblyNodeId,
  ])
  if (nodeIds.some((nodeId) => candidateIds.has(nodeId))) {
    return true
  }

  return node.children.some((child) => hasHierarchyNodeIdentity(child, candidateIds))
}

/**
 * 判断 composed assembly 是否能通过组件身份命中当前历史快照。
 *
 * 设计说明：
 * history 快照的 assemblyId 不是唯一可靠来源。旧数据、拖拽路径或缺少装配上下文的
 * Object3D 会使用 default-assembly，因此必须回退到组件节点身份，才能把快照投影回
 * 当前 loader 按 tabId 缓存的 hierarchy。
 *
 * @param result 当前已加载的 composed assembly 结果。
 * @param snapshot 装配组件历史快照。
 * @returns 能从 hierarchy 或 manifest 找到组件时返回 true。
 */
function matchesAssemblyHistoryComponentIdentity(
  result: ComposedAssemblyImportedModelResult,
  snapshot: AssemblyComponentHistorySnapshot,
): boolean {
  const candidateIds = collectSnapshotComponentIdentityIds(snapshot)
  if (candidateIds.length === 0) {
    return false
  }
  const candidateIdSet = new Set(candidateIds)

  if (hasHierarchyNodeIdentity(result.hierarchy, candidateIdSet)) {
    return true
  }

  return Object.keys(result.manifest.nodesById)
    .some((nodeId) => matchesAssemblyHistoryIdentityLookup(candidateIdSet, nodeId))
}

/**
 * 根据装配历史快照解析当前加载的装配结果。
 *
 * @param loadedResultByTabId 当前 loader 维护的导入结果缓存。
 * @param snapshot 装配组件历史快照。
 * @returns 命中的标签页与装配结果。
 */
export function resolveAssemblyHistoryTarget(
  loadedResultByTabId: ReadonlyMap<string, ImportedModelResult>,
  snapshot: AssemblyComponentHistorySnapshot,
): { tabId: string; result: ComposedAssemblyImportedModelResult } | null {
  const directResult = loadedResultByTabId.get(snapshot.assemblyId)
  if (directResult?.kind === 'composed-assembly') {
    return { tabId: snapshot.assemblyId, result: directResult }
  }

  for (const [tabId, result] of loadedResultByTabId.entries()) {
    if (result.kind !== 'composed-assembly') {
      continue
    }

    const candidateIds = [
      tabId,
      result.fileInfo.fileId,
      result.fileInfo.uuid,
      result.manifest.assemblyFileId,
      result.manifest.assemblyFileUuid,
    ]
    if (candidateIds.includes(snapshot.assemblyId)) {
      return { tabId, result }
    }
  }

  for (const [tabId, result] of loadedResultByTabId.entries()) {
    if (result.kind !== 'composed-assembly') {
      continue
    }

    /**
     * assemblyId 可能是旧路径生成的 default-assembly。只有在所有装配身份都未命中后，
     * 才允许按组件身份兜底，避免多个装配缓存并存时被同名/同 ID 组件提前抢走。
     */
    if (matchesAssemblyHistoryComponentIdentity(result, snapshot)) {
      return { tabId, result }
    }
  }

  return null
}

/**
 * 将历史快照身份交给 SDK 逻辑组件解析，UI 不解释渲染树或 imported metadata。
 *
 * @param viewer SDK 组件查询端口。
 * @param snapshot 装配组件历史快照。
 * @returns 当前可写组件根；缺失或身份歧义时保持 null，由历史事务统一报错回滚。
 */
export function resolveAssemblyHistorySceneObject(
  viewer: AssemblyHistoryProjectionViewer | null | undefined,
  snapshot: AssemblyComponentHistorySnapshot,
): THREE.Object3D | null {
  return viewer?.resolveAssemblyComponentObject(snapshot.componentInstanceId)
    ?? (snapshot.sourceNodeId
      ? viewer?.resolveAssemblyComponentObject(snapshot.sourceNodeId)
      : null)
    ?? null
}

/**
 * 将历史快照中的局部 transform 转为 three.js 列主序矩阵。
 *
 * @param snapshot 装配组件历史快照。
 * @returns 16 元素矩阵。
 */
function createMatrixTupleFromAssemblyHistorySnapshot(
  snapshot: AssemblyComponentHistorySnapshot,
): [
  number, number, number, number,
  number, number, number, number,
  number, number, number, number,
  number, number, number, number,
] {
  const position = new THREE.Vector3(
    snapshot.transform.position[0],
    snapshot.transform.position[1],
    snapshot.transform.position[2],
  )
  const quaternion = new THREE.Quaternion(
    snapshot.transform.quaternion[0],
    snapshot.transform.quaternion[1],
    snapshot.transform.quaternion[2],
    snapshot.transform.quaternion[3],
  )
  const scale = new THREE.Vector3(
    snapshot.transform.scale[0],
    snapshot.transform.scale[1],
    snapshot.transform.scale[2],
  )
  const matrix = new THREE.Matrix4().compose(position, quaternion, scale)
  const elements = matrix.elements

  return [
    elements[0], elements[1], elements[2], elements[3],
    elements[4], elements[5], elements[6], elements[7],
    elements[8], elements[9], elements[10], elements[11],
    elements[12], elements[13], elements[14], elements[15],
  ]
}

/**
 * 按稳定身份解析 manifest 节点 ID。
 *
 * @param result 当前装配结果。
 * @param candidateNodeIds 历史快照与场景对象提供的候选节点 ID。
 * @returns 命中的 manifest 节点 ID；未命中时返回 null。
 */
function resolveManifestNodeIdByAssemblyHistoryIdentity(
  result: ComposedAssemblyImportedModelResult,
  candidateNodeIds: readonly unknown[],
): string | null {
  const manifestNodeIds = Object.keys(result.manifest.nodesById)
  const exactCandidateIds = new Set(collectAssemblyHistoryExactIdentityIds(candidateNodeIds))
  const exactNodeId = manifestNodeIds
    .find((nodeId) => matchesAssemblyHistoryExactIdentity(exactCandidateIds, nodeId))
  if (exactNodeId) {
    return exactNodeId
  }

  const candidateIdSet = new Set(collectAssemblyHistoryIdentityLookupIds(candidateNodeIds))
  if (candidateIdSet.size === 0) {
    return null
  }

  const fuzzyMatches = manifestNodeIds
    .filter((nodeId) => matchesAssemblyHistoryIdentityLookup(candidateIdSet, nodeId))
  return fuzzyMatches.length === 1 ? fuzzyMatches[0] ?? null : null
}

/**
 * 把组件位姿同步回当前导入装配 manifest。
 *
 * @param result 当前装配结果。
 * @param snapshot 装配组件历史快照。
 * @param sceneObject 当前历史快照对应的场景对象。
 * @returns 命中并更新 manifest 节点时返回 true。
 */
export function applyAssemblyTransformToManifestNode(
  result: ComposedAssemblyImportedModelResult,
  snapshot: AssemblyComponentHistorySnapshot,
  sceneObject: THREE.Object3D | null,
): boolean {
  const candidateNodeIds = [
    snapshot.sourceNodeId,
    snapshot.componentInstanceId,
    typeof sceneObject?.userData?.assemblyNodeId === 'string' ? sceneObject.userData.assemblyNodeId : '',
  ].filter((nodeId): nodeId is string => typeof nodeId === 'string' && nodeId.trim().length > 0)
  const targetNodeId = resolveManifestNodeIdByAssemblyHistoryIdentity(result, candidateNodeIds)
  if (!targetNodeId) {
    return false
  }

  const targetNode = result.manifest.nodesById[targetNodeId]
  if (!targetNode) {
    return false
  }

  result.manifest.nodesById[targetNodeId] = {
    ...targetNode,
    transform: createMatrixTupleFromAssemblyHistorySnapshot(snapshot),
  }
  return true
}
